/**
 * DnDCanvas provides a reorderable grid layout.
 *
 * Firstly, it makes a flex box grid layout for any non-empty 2-dimensional array of generic-typed items. Flex
 * box decides the width of the blocks within the grid, and every column will be "filled" except the the last.
 * There are a few considerations that this implies:
 *  - The rows will always be 100% width
 *  - The width of the blocks will be row_width/num_columns except for the last row
 *  - The above reality should imply that if the last row has 1 block, it will be maximum width. This is not
 *  pretty for the case where we expect every row to have the same length expect the last^. In this case every
 *  block has the same size except for the last row's blocks, so we add a maxColumnCount prop, which is used to
 *  calculate the width of the blocks in the last row using row_width/maxColumnCount instead. (See the usage of
 *  DnDPlaceholderBlock definition below for more details.)
 *
 *  ^DnDCanvas is developed assuming that it will only be used with layouts that have the same number of
 *  blocks per row except the last, so using a layout that has a different number of blocks per row might
 *  will not behave as nicely. This assumption is not checked because we plan on expanding the features in
 *  later work.
 *
 * Secondly, DnDCanvas handles dragging the blocks. It will handle all drag associated interactions like
 * hovering over blocks, picking up block, previewing placement, moving the mouse while dragging, and scrolling
 * while dragging.
 *
 * Finally, DnDCanvas does not handle dropping blocks. Instead it calls a callback, which the consumer specifies.
 * This callback says which block was dropped ("source item") and onto what block it was dropped ("target item").
 * The consumer is then expected to decide the new layout based on that. The onMoveItem callback also
 * receives a hoverPosition ('left' | 'right') indicating which side of the target the source was dropped on.
 * See moveSourceAdjacentToTarget for a move-item-handler that uses this to insert on either side.
 *
 * Example, the moveSourceAdjacentToTarget move-item-handler considers the layout as a "list", so if we start
 * with the following layout:
 *
 * [block 1][block 2]
 * [block 3][block 4]
 *
 * And drag block 3 onto block 1 (source item = block 3, target item = block 1), we get the move all the blocks
 * after block 1 by 1, and drop it as if the layout is a list arranged in a grid. The result is the following:
 *
 * [block 1][block 3]
 * [block 2][block 4]
 *
 * Possible alternatives could be swapping the blocks, which is the:
 *
 * [block 3][block 2]
 * [block 1][block 4]
 *
 * Or inserting within that row, which would yield the following layout if maxColumnCount=2:
 *
 * [block 1][block 3][block 2]
 * [block      4]
 *
 * Or this if maxColumnCount=1
 *
 * [block 1][block 3][block 2]
 * [block                   4]
 */
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import useDocumentCursor from 'ui-library/hooks/useDocumentCursor';

import DnDBlock, {DnDItemRenderFunction, DnDPlaceholderBlock} from './DnDBlock';
import {DND_BLOCK_SPACING} from './DnDStyleConstants';
import useDnDCanvasDragState, {HoverPosition} from './hooks/useDnDCanvasDragState';

export interface DnDCanvasProps<T> {
  items: Array<Array<T>>;
  blockHeight?: number;
  maxColumnCount: number;
  minBlockWidth?: number;
  /** When true, blocks size to their content instead of growing to fill available space */
  fitContent?: boolean;
  onMoveItem: (sourceItem: T, targetItem: T, hoverPosition?: HoverPosition) => void;
  renderItem: DnDItemRenderFunction<T>;
  disabledItems?: Array<number>;
  itemContainers?: Array<(props: {children: JSX.Element}) => JSX.Element>;
  canvasContainer?: (props: {children: JSX.Element}) => JSX.Element;
  disableHorizontalDrag?: boolean;
  /** Extract a stable key from an item. Falls back to positional `${rowIndex}-${colIndex}` keys. */
  getItemKey?: (item: T) => string;
}

export default function DnDCanvas<T>(props: DnDCanvasProps<T>): JSX.Element {
  const {dragState, onItemDragStart, onItemHoverEnter, onItemHoverLeave} = useDnDCanvasDragState<T>({
    onMoveItem: props.onMoveItem,
  });
  useDocumentCursor('grabbing', dragState.isDragging);

  assert(props.items.length > 0, 'Expected the layout to be non-empty');

  if (props.itemContainers) {
    assert(
      props.itemContainers.length === props.items.length,
      'Item container, if provided, and row counts must be the same length'
    );
  }

  const items = props.items.map((row, rowIndex) => {
    const renderedRow = (
      <>
        {row.map((col, colIndex) => {
          // The index of a block is defined as its index if the rows were all folded into one row.
          //
          // For example:
          // [0, 1, 2, 3]
          // [4, 5, 6, 7]
          const blockIndex = props.maxColumnCount * rowIndex + colIndex;
          return (
            <DnDBlock
              dragState={dragState}
              blockHeight={props.blockHeight}
              item={col}
              index={blockIndex}
              key={props.getItemKey ? props.getItemKey(col) : `${rowIndex}-${colIndex}`}
              minBlockWidth={props.minBlockWidth}
              fitContent={props.fitContent}
              onDragStart={onItemDragStart}
              onMouseEnterBlock={onItemHoverEnter}
              onMouseLeaveBlock={onItemHoverLeave}
              renderItem={props.renderItem}
              disabled={props.disabledItems != null && props.disabledItems.includes(blockIndex)}
              disableHorizontalDrag={props.disableHorizontalDrag}
            />
          );
        })}
      </>
    );
    if (props.itemContainers) {
      return props.itemContainers[rowIndex]({children: renderedRow});
    } else {
      return (
        <div
          className={css(styles.row)}
          key={rowIndex}
          {...getTestProps({component: 'DnDCanvas', element: 'row'})}
        >
          {renderedRow}

          {rowIndex === props.items.length - 1
            ? // Using flex box as row column layout without explicitly using responsive css width properties
              // (e.g. width = 50%) means that the blocks will always grow to fit the max width. This is great
              // for putting the layout in any size of div, but the problem is that if we have a layout like
              // the following:
              //
              // [block 1, block 2, block 3]
              // [block                   4]
              //
              // Item 4 will be triple the width of block 1, 2, and 3. In the case of Insights's layout, this
              // inconsistency is unacceptable until we give users more control over block widths. The solution
              // is to add placeholder blocks in the layout like so:
              //
              // [block 1, block 2    , block 3    ]
              // [block 4, placeholder, placeholder]
              //
              // These placeholders are invisible divs that have no action listeners, so they only add spacing
              // constraints and no other functionality.
              // eslint-disable-next-line no-restricted-syntax
              _.times(props.maxColumnCount - row.length, (colIndex) => (
                <DnDPlaceholderBlock
                  key={`${rowIndex}-${row.length + colIndex}`}
                  minBlockWidth={props.minBlockWidth}
                />
              ))
            : null}
        </div>
      );
    }
  });

  return (
    // If we don't disable user selection while dragging, then a lot of the text in the blocks and surrounding
    // areas will be selected while dragging, which is the default "hold mouse and move" feature of browsers.
    <div
      className={classNames(
        {'u-no-select': dragState.isDragging},
        props.canvasContainer
          ? css(styles.dndCanvasWithContainer)
          : css(styles.dndCanvas, props.disableHorizontalDrag && styles.dndCanvasDisableHorizontalDrag)
      )}
      {...getTestProps({component: 'DnDCanvas', element: 'root'})}
    >
      {props.canvasContainer ? props.canvasContainer({children: <>{items}</>}) : items}
    </div>
  );
}

const styles = StyleSheet.create({
  dndCanvas: {
    // If the overflow is visible, it is possible to drag the block off the screen, which makes the DnDCanvas
    // div bigger, and this allows the user to scroll beyond the height of the layout.
    overflow: 'hidden',
    minHeight: '100%',
    // Horizontal spacing is split symmetrically between DnDCanvas and DnDBlock:
    //  - DnDCanvas adds paddingLeft of SPACING/2.
    //  - Each DnDBlock adds SPACING/2 on both its left and right sides (border + padding + margin).
    //  - Two adjacent blocks therefore produce the full column spacing between them (SPACING/2 + SPACING/2).
    //  - The canvas paddingLeft plus the first block's left side produces the full left-edge spacing.
    //
    // DnDCanvas defines the full top spacing, and DnDBlock defines the full bottom spacing.
    //
    // Example ("|" and "-" represent padding):
    //
    // Full screen:
    // -------------------
    // |[pane 1]|[pane 2]|
    // -------------------
    // |[pane 3]|[pane 4]|
    // -------------------
    //
    // Half screen:
    // ----------
    // |[pane 1]|
    // ----------
    // |[pane 2]|
    // ----------
    // |[pane 3]|
    // ----------
    // |[pane 4]|
    // ----------
    //
    // Blocks control their own border styling because it is used for the drop-item hover preview.
    paddingTop: DND_BLOCK_SPACING,
    paddingLeft: DND_BLOCK_SPACING / 2,
  },
  dndCanvasDisableHorizontalDrag: {
    paddingBottom: DND_BLOCK_SPACING,
  },
  dndCanvasWithContainer: {overflowX: 'hidden', overflowY: 'scroll'},
  row: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
});
